import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Flame, Wrench, Hammer, Sparkles, ArrowRight, Check, FileSignature, ShieldCheck, Wallet, CalendarClock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Flame: Flame,
  Wrench: Wrench,
  Hammer: Hammer,
  Sparkles: Sparkles,
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    // Handle hash navigation
    if (window.location.hash) {
      const element = document.querySelector(window.location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [services]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <main className="pt-24 pb-16" data-testid="services-page">
      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#00F0FF] font-medium tracking-wider uppercase text-sm">
              Our Expertise
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mt-4 mb-6">
              Professional <span className="gradient-text">Services</span>
            </h1>
            <p className="text-gray-400 max-w-2xl text-lg">
              From heavy-duty industrial work to meticulous cleaning, our skilled team 
              delivers exceptional results across all trade services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto space-y-24">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            const isEven = index % 2 === 0;
            
            return (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                data-testid={`service-detail-${service.id}`}
              >
                {/* Image */}
                <div className="lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden trace-border">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-[400px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020408] via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-14 h-14 rounded-xl bg-[#00F0FF]/20 border border-[#00F0FF]/50 flex items-center justify-center cyan-glow">
                        <Icon className="w-7 h-7 text-[#00F0FF]" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2">
                  <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                    {service.name}
                  </h2>
                  <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-md bg-[#008F7A]/20 flex items-center justify-center">
                          <Check className="w-4 h-4 text-[#00F0FF]" />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/booking">
                    <Button
                      data-testid={`book-${service.id}-button`}
                      className="btn-primary rounded-none px-8 py-5 font-heading tracking-wide group"
                    >
                      BOOK {service.name.toUpperCase()}
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Contracts & Deposits */}
      <section className="px-6 md:px-12 lg:px-24 pt-24" data-testid="contracts-deposits-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <span className="text-[#00F0FF] font-medium tracking-wider uppercase text-sm">
              Contracts, Agreements & Deposits
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4 mb-4">
              Built for <span className="gradient-text">Long-Term Trust</span>
            </h2>
            <p className="text-gray-400 max-w-2xl text-lg">
              Whether you need a one-time job or an ongoing partnership, our flexible
              contracts, confidentiality options, and clear deposit policy keep everything
              transparent.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contracts & Agreements Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="glass rounded-2xl p-8 md:p-10"
              data-testid="contracts-card"
            >
              <div className="w-14 h-14 rounded-xl bg-[#00F0FF]/20 border border-[#00F0FF]/50 flex items-center justify-center cyan-glow mb-6">
                <FileSignature className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Contracts & Agreements</h3>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#008F7A]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <span>
                    <strong className="text-white">Service contracts available.</strong> Set up an
                    ongoing agreement tailored to your schedule and needs.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#008F7A]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <span>
                    <strong className="text-white">Prepay & save.</strong> When a contract is in
                    place, each cleaning is slightly discounted if the monthly payment is prepaid.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#008F7A]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <ShieldCheck className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <span>
                    <strong className="text-white">Non-Disclosure Agreements.</strong> We offer NDAs
                    to anyone who may ask — your privacy and confidentiality come first.
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Deposit Policy Card */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="glass rounded-2xl p-8 md:p-10"
              data-testid="deposit-card"
            >
              <div className="w-14 h-14 rounded-xl bg-[#00F0FF]/20 border border-[#00F0FF]/50 flex items-center justify-center cyan-glow mb-6">
                <Wallet className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-4">Payment & Deposits</h3>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#008F7A]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Wallet className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <span>
                    <strong className="text-white">Deposits for non-contract clients.</strong> A
                    deposit is required to hold every appointment — for your first booking and all
                    future bookings.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#008F7A]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <ShieldCheck className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <span>
                    <strong className="text-white">First appointment always requires a deposit.</strong> Even
                    if you're requesting a contract, a deposit is required for your first initial
                    appointment.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#008F7A]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <CalendarClock className="w-4 h-4 text-[#00F0FF]" />
                  </div>
                  <span>
                    <strong className="text-white">24-hour cancellation rule.</strong> If a contract
                    client cancels an appointment without 24 hours' notice, payment for the scheduled
                    day in question will not be returned.
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              Not Sure Which Service You Need?
            </h3>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Contact us for a free consultation. We'll assess your needs and 
              recommend the best solution.
            </p>
            <Link to="/contact">
              <Button
                data-testid="services-contact-cta"
                variant="outline"
                className="btn-outline rounded-none px-8 py-5 font-heading tracking-wide"
              >
                GET FREE CONSULTATION
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
